/**
 * A WhatsApp voice note or PDF inside a message bubble (tasks/whatsapp-inbox-4jawaly.md).
 *
 * Fetched only when pressed, not on opening the thread: voice notes and PDFs are bigger than photos, and most aren't
 * opened twice. As with photos, the bytes come through the authenticated API client and are wrapped in an object URL,
 * so this works where a plain link couldn't. A failure, or a voice note this browser can't play, falls back to the
 * label that sends staff to the ops phone.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { conversationApi } from '../../services/conversationApi';
import { AttachmentLabel } from './MessageAttachment';

export default function WhatsAppFile({ conversationId, messageId, file, fallbackLabel }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const urlRef = useRef(null);
  const leftRef = useRef(false);

  // An inbox tab stays open all shift, so an object URL that's never released adds up.
  useEffect(() => {
    leftRef.current = false;
    return () => {
      leftRef.current = true;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    };
  }, []);

  const load = async () => {
    if (urlRef.current) return urlRef.current;
    const blob = await conversationApi.whatsappFile(conversationId, messageId);
    // Staff left the thread while it loaded, and nothing would release a URL made now.
    if (leftRef.current) return null;
    urlRef.current = URL.createObjectURL(blob);
    return urlRef.current;
  };

  const playVoiceNote = async () => {
    setBusy(true);
    try {
      setUrl(await load());
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const openPdf = async () => {
    // Opened before the wait: a browser blocks a new tab that isn't the direct result of a tap.
    const tab = window.open('', '_blank');
    setBusy(true);
    try {
      const objectUrl = await load();
      if (tab && objectUrl) tab.location.href = objectUrl;
      else if (tab) tab.close();
      else setUrl(objectUrl);
    } catch {
      if (tab) tab.close();
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  if (failed) return <AttachmentLabel text={fallbackLabel} />;

  if (file.kind === 'audio') {
    if (url) return <audio className="inbox-wa-audio" src={url} controls autoPlay aria-label="رسالة صوتية" onError={() => setFailed(true)} />;
    return (
      <button type="button" className="inbox-wa-file" onClick={playVoiceNote} disabled={busy}>
        {busy ? <Spinner animation="border" size="sm" /> : <i className="bi bi-play-fill" aria-hidden="true"></i>}
        <span>تشغيل الرسالة الصوتية</span>
      </button>
    );
  }

  // The tab was blocked, so offer the file as a plain link the next tap can follow.
  if (url) {
    return (
      <a className="inbox-wa-file" href={url} target="_blank" rel="noreferrer">
        <i className="bi bi-file-earmark-pdf" aria-hidden="true"></i>
        <bdi>{file.filename || 'فتح المستند'}</bdi>
      </a>
    );
  }
  return (
    <button type="button" className="inbox-wa-file" onClick={openPdf} disabled={busy}>
      {busy ? <Spinner animation="border" size="sm" /> : <i className="bi bi-file-earmark-pdf" aria-hidden="true"></i>}
      <bdi>{file.filename || 'فتح المستند'}</bdi>
    </button>
  );
}
